import { Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { Box, Button, Card, CardContent, CardHeader, Divider, Grid, LinearProgress, Pagination, TextField } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { DataGrid } from '@mui/x-data-grid';
import appUrls from 'appUrls';
import CommonPageDataImportFileForm from 'components/common_page_data/CommonPageDataImportFileForm';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import { deleteFolderRequest, getCommonPageDataListRequest, importCommonPageDataRequest } from 'services/common_page_data';
import Swal from 'sweetalert2';
import { useDebounce } from 'use-debounce';
import { downloadCommonPageData } from 'utils/excel.utils';
import { parseJsonFromXLSX } from 'utils/excelUtils';
import Breadcrumb from '../../components/ui/Breadcrumb';
import { limitOptions } from '../../data';
import { instituteSortOptions } from '../../data/institute';
import { selectStyles } from '../../libs/formik_fields/SelectField';

const useStyles = makeStyles((theme) => ({
    mainHeading: {
        margin: theme.spacing(1),
        [theme.breakpoints.down('md')]: {
            textAlign: 'center'
        }
    },
    breadcrumbs: {
        display: 'flex',
        [theme.breakpoints.down('md')]: {
            justifyContent: 'center'
        }
    },
    clientCardsContainer: {
        padding: theme.spacing(3)
        // marginTop: theme.spacing(1)
    },
    progressBarContainer: {
        height: 5
        // marginTop: 10,
        // marginBottom: 10
    },
    button: {
        marginLeft: theme.spacing(1),
        marginRight: theme.spacing(1)
    }
}));

// eslint-disable-next-line no-unused-vars
function CommonPageData(props) {
    const classes = useStyles();
    const navigate = useNavigate();

    const [itemsList, setItemsList] = useState([]);
    const [search, setSearch] = useState('');
    const [debouncedSearch] = useDebounce(search, 1000);
    const [isLoading, setIsLoading] = useState(true);
    const [sortOrder, setSortOrder] = useState(instituteSortOptions[0]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(10);
    const [limit, setLimit] = useState(limitOptions[0]);
    const [deleteLoading, setDeleteLoading] = useState('');

    const [mutation, setMutation] = useState();

    async function fetchData() {
        setIsLoading(true);
        const sortValue = sortOrder.value;
        const data = await getCommonPageDataListRequest({
            search: debouncedSearch,
            sortOrder: sortValue,
            page,
            limit: limit.value || 12
        });
        setIsLoading(false);
        if (data.error === false) {
            setItemsList(
                data.results.map((x, index) => ({
                    ...x,
                    rowId: index + 1
                }))
            );
            setPage(data.page);
            setTotalPages(data.totalPages);
        }
    }

    useEffect(() => {
        fetchData();
    }, [debouncedSearch, sortOrder, page, limit, mutation]);

    const handleImportFile = async (values, formikHelpers) => {
        const { file } = values;
        if (file) {
            const data = await parseJsonFromXLSX(file);
            const titles = data[Object.keys(data)[0]]?.map((obj) => {
                const { TITLE: name } = obj;
                return { name };
            });

            const response = await importCommonPageDataRequest({ titles });

            if (response.error === false) {
                setMutation({});
            }
        }
        formikHelpers.setSubmitting(false);
    };

    const handleDelete = async (folderId) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: `You won't be able to revert this, Proceeding further will delete this folder!`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes',
            cancelButtonText: 'No'
        });

        if (result.isConfirmed) {
            setDeleteLoading(folderId);
            const data = await deleteFolderRequest({ folderId });
            setDeleteLoading('');
            if (data.error === false) {
                fetchData();
            }
        }
    };

    const columns = [
        // {
        //     field: 'rowId',
        //     headerName: 'Sr.No.',
        //     flex: 2
        // },
        {
            field: 'title',
            headerName: 'Title',
            flex: 10,
            renderCell: (params) => <div>{params.row.name ? params.row.name : '-'}</div>
        },
        {
            field: 'action',
            headerName: 'Action',
            flex: 1,
            renderCell: (params) => (
                <div>
                    <Button
                        disableElevation
                        variant="outlined"
                        color="primary"
                        size="medium"
                        className={classes.button}
                        // fullWidth
                        onClick={() => {
                            navigate(`${appUrls.COMMON_PAGE_DATA}/${params.row?._id}`);
                        }}
                    >
                        <EditIcon />
                    </Button>

                    <LoadingButton
                        disableElevation
                        variant="outlined"
                        color="error"
                        size="medium"
                        className={classes.button}
                        loading={deleteLoading === params.row?._id}
                        loadingPosition="center"
                        onClick={() => handleDelete(params.row?._id)}
                    >
                        <DeleteIcon />
                    </LoadingButton>
                </div>
            )
        }
    ];

    return (
        <>
            <Breadcrumb title="Common Page Data" />
            <Grid container justifyContent="center" spacing={3}>
                <Grid item xs={12}>
                    <Card>
                        <CardHeader
                            title="Import Common Page Data"
                            sx={{
                                pl: 5
                            }}
                            action={
                                <Button
                                    disableElevation
                                    variant="contained"
                                    color="primary"
                                    size="medium"
                                    onClick={() => {
                                        downloadCommonPageData();
                                    }}
                                >
                                    Download Sample Xlsx
                                </Button>
                            }
                        />
                        <Divider />
                        <CardContent>
                            <Box p={1}>
                                <CommonPageDataImportFileForm handleSubmit={handleImportFile} />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12}>
                    <Card>
                        <CardContent
                            sx={{
                                p: 0
                            }}
                        >
                            <Grid container>
                                <Grid item xs={12}>
                                    <Grid container justifyContent="space-between">
                                        <Grid item xs={12} sm={7} md={6} lg={4} xl={3}>
                                            <Grid container>
                                                <Grid item xs={12} sm={6}>
                                                    <Box
                                                        sx={{
                                                            py: 3,
                                                            pl: 3,
                                                            pr: {
                                                                xs: 3,
                                                                sm: 0
                                                            }
                                                        }}
                                                    >
                                                        <TextField
                                                            id="search"
                                                            label="Search"
                                                            type="search"
                                                            variant="outlined"
                                                            size="small"
                                                            value={search}
                                                            onChange={(e) => setSearch(e.target.value)}
                                                            fullWidth
                                                        />
                                                    </Box>
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <Box
                                                        width="100%"
                                                        sx={{
                                                            py: {
                                                                sm: 3
                                                            },
                                                            px: 3,
                                                            borderRadius: 10,
                                                            background: 'white'
                                                        }}
                                                    >
                                                        <Select
                                                            styles={selectStyles}
                                                            placeholder="Sort by"
                                                            value={sortOrder}
                                                            options={instituteSortOptions}
                                                            onChange={(e) => setSortOrder(e)}
                                                            label="Sort by"
                                                        />
                                                    </Box>
                                                </Grid>
                                            </Grid>
                                            {/* <Box */}
                                            {/*    sx={{ */}
                                            {/*        display: 'flex', */}
                                            {/*        alignItems: 'center', */}
                                            {/*        p: 3, */}
                                            {/*        pb:{ */}
                                            {/*            xs:0, */}
                                            {/*            md:3 */}
                                            {/*        }, */}
                                            {/*    }} */}
                                            {/* > */}
                                            {/*   */}
                                            {/*    */}
                                            {/* </Box> */}
                                        </Grid>
                                        <Grid item xs={12} sm={5} md={6}>
                                            <Box
                                                p={3}
                                                display="flex"
                                                justifyContent="flex-end"
                                                sx={{
                                                    justifyContent: {
                                                        xs: 'center',
                                                        md: 'flex-end'
                                                    }
                                                }}
                                            >
                                                {/* <Button
                                                    className={classes.button}
                                                    variant="outlined"
                                                    color="primary"
                                                    sx={{
                                                        mr: 1
                                                    }}
                                                    onClick={() => navigate(appUrls.INSTITUTE_ADD)}
                                                    disableElevation
                                                    startIcon={<AddIcon />}
                                                >
                                                    Add Institute
                                                </Button> */}
                                            </Box>
                                        </Grid>
                                    </Grid>
                                    {/* <Box */}
                                    {/*    width="100%" */}
                                    {/*    sx={{ */}
                                    {/*        display: 'flex', */}
                                    {/*        justifyContent: 'space-between', */}
                                    {/*        alignItems: 'center', */}
                                    {/*        p: 3 */}
                                    {/*    }} */}
                                    {/* > */}
                                    {/*    */}
                                    {/*   */}
                                    {/* </Box> */}
                                    {/* <Divider /> */}
                                </Grid>

                                <Grid item xs={12}>
                                    <Box className={classes.progressBarContainer}>{isLoading && <LinearProgress />}</Box>

                                    <Box px={3}>
                                        <DataGrid
                                            rows={itemsList}
                                            columns={columns}
                                            getRowId={(row) => row._id}
                                            autoHeight
                                            disableColumnMenu
                                            paginationMode="server"
                                            hideFooterPagination
                                            hideFooter
                                            isRowSelectable={false}
                                        />
                                    </Box>

                                    {/* <Grid className={classes.clientCardsContainer} container spacing={3}>
                                        {itemsList.map((student) => (
                                            <Grid key={student._id} item xs={12} md={6} lg={4} xl={3}>
                                                <StudentCard
                                                    totalGallery={totalGallery}
                                                    totalPollQuestions={totalPollQuestions}
                                                    totalProfileQuestions={totalProfileQuestions}
                                                    key={student._id}
                                                    {...student}
                                                    refetchList={refetchList}
                                                />
                                            </Grid>
                                        ))}
                                        {isInitialLoading &&
                                            [...Array(12)].map((_, i) => (
                                                <Grid key={i} item xs={12} md={6} lg={4} xl={3}>
                                                    <Skeleton variant="rect" height={300} />
                                                </Grid>
                                            ))}
                                        {!isInitialLoading && !isLoading && itemsList.length === 0 ? (
                                            <Grid item xs={12}>
                                                <Box width="100%" p={5} bgcolor="white" borderRadius={3}>
                                                    <Typography variant="h3" align="center">
                                                        No Student found
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        ) : null}
                                    </Grid> */}
                                </Grid>

                                <Grid item xs={12} mt={3}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={8} lg={10}>
                                            <Box
                                                sx={{
                                                    px: 2,
                                                    py: {
                                                        xs: 1,
                                                        md: 0
                                                    },
                                                    display: 'flex',
                                                    justifyContent: {
                                                        xs: 'center',
                                                        md: 'flex-start'
                                                    }
                                                }}
                                            >
                                                {totalPages > 0 ? (
                                                    <Pagination
                                                        color="primary"
                                                        count={totalPages}
                                                        page={page}
                                                        onChange={(event, page) => {
                                                            setPage(page);
                                                        }}
                                                    />
                                                ) : null}
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} md={4} lg={2} justifyContent="center">
                                            <Box
                                                width="100%"
                                                sx={{
                                                    px: 3,
                                                    py: {
                                                        xs: 1,
                                                        md: 0
                                                    }
                                                }}
                                            >
                                                <Select
                                                    styles={selectStyles}
                                                    placeholder="Items Per Page"
                                                    value={limit}
                                                    options={limitOptions}
                                                    onChange={(e) => setLimit(e)}
                                                    label="Items Per Page"
                                                    menuPlacement="top"
                                                />
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </>
    );
}

export default CommonPageData;
